import type { CSSProperties } from 'react';
import { useDuration } from './shared/useDuration';
import { CachedImage } from './shared/CachedImage';
import { BrandLogo } from './shared/BrandLogo';
import { brandTokens, fontFamilyFor } from './shared/tokens';
import type { ManifestPromoItem } from '../types';

interface PromoBurstProps {
  item: ManifestPromoItem;
  onEnd: () => void;
}

// promo-burst — full-bleed promotional layout for "Lunch Special",
// "Today Only", "Call Sara", etc.
//
// Visual rules (per M2E.D scope):
//   - full-bleed promo.background with dual-gradient overlay for text
//     legibility (vignette + bottom shade); radial accent-tint
//     gradient fallback if no background image
//   - huge accent-font headline with subtle text-shadow for depth
//   - subheadline below at reduced opacity for hierarchy
//   - CTA chip with pill radius, accent background, dark text,
//     soft inset shadow
//   - brand logo bottom-right (deliberately different from MenuClassic's
//     top-right placement so layouts read distinctly)
//
// 16:9 landscape is the design target; portrait works without changes
// because the center stack is content-driven, not grid-constrained.
export function PromoBurst({ item, onEnd }: PromoBurstProps) {
  useDuration(item.durationMs, onEnd);

  const { brand, promo } = item;
  const accent = promo.accentColor ?? brand.primaryColor ?? '#ff6b35';
  const accentFontStack = fontFamilyFor(brand.accentFont);

  return (
    <div style={{ ...rootStyle, ...brandTokens(brand) }}>
      {/* Background layer: image + dual-gradient overlay, OR radial accent gradient */}
      {promo.background ? (
        <>
          <CachedImage mediaRef={promo.background} style={bgImageStyle} />
          <div style={bgOverlayStyle} />
        </>
      ) : (
        <div
          style={{
            ...bgGradientStyle,
            background: `radial-gradient(ellipse at center, ${accent}33 0%, #0a0a0a 70%), #0a0a0a`,
          }}
        />
      )}

      {/* Centered content stack */}
      <div style={contentStyle}>
        <h1
          style={{
            ...headlineStyle,
            fontFamily: accentFontStack,
          }}
        >
          {promo.headline}
        </h1>

        {promo.subheadline ? (
          <div style={subheadlineStyle}>{promo.subheadline}</div>
        ) : null}

        {promo.ctaText ? (
          <div
            style={{
              ...ctaStyle,
              background: accent,
              fontFamily: accentFontStack,
            }}
          >
            {promo.ctaText}
          </div>
        ) : null}
      </div>

      <BrandLogo brand={brand} position="bottom-right" maxHeight="8vh" />
    </div>
  );
}

// ---------- Styles ----------

const rootStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  background: '#0a0a0a',
  color: '#ffffff',
  overflow: 'hidden',
  fontFamily: '"Inter", system-ui, sans-serif',
};

const bgImageStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0,
};

const bgOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.78) 80%), ' +
    'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)',
  zIndex: 1,
};

const bgGradientStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 0,
};

const contentStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6vh 6vw',
  boxSizing: 'border-box',
  textAlign: 'center',
  gap: '3vh',
};

const headlineStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(3rem, 10vw, 12rem)',
  fontWeight: 800,
  lineHeight: 1.0,
  letterSpacing: '-0.02em',
  color: '#ffffff',
  textShadow: '0 4px 24px rgba(0,0,0,0.45)',
  maxWidth: '92%',
};

const subheadlineStyle: CSSProperties = {
  fontSize: 'clamp(1.2rem, 3vw, 2.6rem)',
  fontWeight: 300,
  lineHeight: 1.25,
  opacity: 0.88,
  maxWidth: '80%',
  letterSpacing: '0.01em',
};

const ctaStyle: CSSProperties = {
  display: 'inline-block',
  marginTop: '1.5vh',
  padding: '1.1em 2.4em',
  borderRadius: '999px',
  fontSize: 'clamp(1.1rem, 2.2vw, 2rem)',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#0a0a0a',
  boxShadow:
    '0 6px 24px rgba(0,0,0,0.35), inset 0 -3px 0 rgba(0,0,0,0.15)',
  lineHeight: 1,
};
